import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";

import { logger } from "src/core";
import { MessageManager } from "src/core/streaming/core/messageManager";
import {
  ActivityEvent,
  FabricCandidate,
  FabricConfig,
  GetLimitsResponse,
  TenantObjects
} from "src/gen/schema/configd/api";
import { FabricMultisite } from "src/gen/schema/models/dci";
import { CandidateType, SoftwareMode } from "src/gen/schema/models/fabric";
import { FabricSoftwareConfig } from "src/gen/schema/models/image";
import { Fabric, Vni, Vrf } from "src/gen/schema/models/models";
import { IpSlaConfig } from "src/gen/schema/models/qos";
import { PerVlanStp } from "src/gen/schema/models/routing";
import { FabricTransaction } from "src/gen/schema/schema/schema";
import { DeviceModelHelper, useDeviceModels } from "src/hooks/useDeviceModels";
import { FabricHelpers, useFabricHelpers } from "src/hooks/useFabricHelpers";
import { Sextant } from "src/utils/api";
import { CandidateMode, ConfigD, KEY_NAME } from "src/utils/api/configD";
import { debounce } from "src/utils/timing";

export const fixContext = () => {
  /**
   * TODO: figure out if there is a better solution
   * This is to bypass a bug
   * with the hot reload. When
   * a file is changed in dev mode
   * the hot reload will reset
   * all context to their default
   * value. this will mostly break
   * any context. To fix the issue
   * forcing a page reload will
   * reset the context back to
   * a working state.
   *
   * This is kind of a sledgehammer
   * for cracking eggs ;)
   */
  window.location.reload();
};

export type CandidateContext = "fabric" | "multisite" | undefined;

interface DataProviderContextSchema extends FabricHelpers, DeviceModelHelper {
  loading: boolean;
  fabric?: Fabric;
  multiSites?: FabricMultisite[];
  softwareConfig: FabricSoftwareConfig | undefined;
  tenants?: TenantObjects[];
  perVlanStp?: PerVlanStp;
  ipslaConfigs?: IpSlaConfig[];
  events: ActivityEvent[];
  candidateType: CandidateType;
  context: CandidateContext; // Current context: fabric or multisite
  mode: CandidateMode; // Candidate mode for both fabric and multisite
  vnis: Vni[];
  vrfs: Vrf[];
  refresh: () => void;
  setMode: (m: CandidateMode) => void;
  setFabricId: (id?: string) => void;
  setMultisiteId: (id?: string) => void;
  configLimits?: GetLimitsResponse;
}

export const DataProviderContext = createContext<DataProviderContextSchema>({
  fabric: undefined,
  softwareConfig: {
    fabricId: "",
    runningImage: undefined,
    targetImage: undefined,
    softwareMode: SoftwareMode.UNRECOGNIZED,
    nodeImages: [],
    switchGroupSwConfs: []
  },
  loading: false,
  events: [],
  candidateType: CandidateType.CANDIDATE_UNSPECIFIED,
  tenants: [],
  ipslaConfigs: [],
  perVlanStp: undefined,
  context: undefined,
  mode: CandidateMode.Edit,
  configLimits: undefined,
  vnis: [],
  vrfs: [],
  multiSites: [],
  /**
   * This is sort of a sledgehammer
   * because the streaming configs
   * don't stream everything we need.
   */
  refresh: fixContext,
  setMode: fixContext,
  setFabricId: fixContext,
  setMultisiteId: fixContext,
  /* FabricHelpers */
  getPortById: () => undefined,
  getPortByName: () => undefined,
  getPortByIndex: () => undefined,
  getNode: () => undefined,
  getPluggableByName: () => undefined,
  /* DeviceModelHelper */
  getDeviceByModelName: () => undefined,
  getDeviceModels: () => [],
  getDeviceModelNames: () => []
});

const WAIT = 75; // 75ms debounce to prevent spamming the API

/**
 * Compares two FabricTransaction arrays to determine if any transactions have changed.
 * Returns true if the arrays differ in length or if any transaction's changedAt timestamp differs.
 * @param prev - Previous array of FabricTransactions
 * @param next - New array of FabricTransactions
 * @returns true if transactions have changed, false otherwise
 */
const hasChanged = (prev: FabricTransaction[], next: FabricTransaction[]) => {
  /* If the length of the arrays differ, then something has changed */
  if (prev.length !== next.length) {
    return true;
  }
  /* If any changedAt value is different, then something has changed */
  return prev.some((p, i) => p.changedAt !== next[i]?.changedAt);
};

interface DataProviderProps extends PropsWithChildren {
  /* Added to keep the existing children from needing to change */
  readonly configLimits?: GetLimitsResponse;
}

export const DataProvider = ({ children, configLimits }: DataProviderProps) => {
  /* The list of pending Fabric Transaction */
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [, setFabricTransaction] = useState<FabricTransaction[]>([]);
  /* Type of candidate configuration */
  const [candidateType, setCandidateType] = useState<CandidateType>(
    CandidateType.CANDIDATE_UNSPECIFIED
  );
  /* The full Fabric config */
  const [fabricConfig, setFabricConfig] = useState<FabricConfig | undefined>();

  const [fabricId, setFabricId] = useState<string | undefined>();
  const [multisiteId, setMultisiteId] = useState<string | undefined>();
  const [mode, _setMode] = useState<CandidateMode>(() => {
    /* Get the value in local storage if there is one. */
    const v = localStorage.getItem(KEY_NAME);
    /**
     * Currently just defaulting to edit, but once
     * we have the changes api the plan is to default
     * to edit if there are pending changes.
     */
    return v ? (v as CandidateMode) : CandidateMode.Edit;
  });
  const [loading, setLoading] = useState<boolean>(true);

  /**
   * Using useMemo to memorize the refresh function since useCallback is
   * not really intended for calling a function and getting a result.
   * Debouncing refresh function to manage the use effect running several
   * times. It was spamming the API and causing issues
   */
  const refresh = useMemo(
    () =>
      debounce(() => {
        if (fabricId) {
          ConfigD.getFabricConfig(mode, fabricId).then(
            (fc: FabricConfig | undefined) => {
              if (fc) {
                setFabricConfig(fc);
              }
              setLoading(false);
            },
            () => {
              setLoading(false);
            }
          );
          /**
           * Loads all the pending Fabric Transactions.
           * There should just be one Candidate with a
           * bunch of activity events
           */
          ConfigD.getFabricTransactions(fabricId)
            .then((t: FabricCandidate[] = []) => {
              if (t[0]) {
                const [candidate] = t;
                setEvents(candidate.events);
                setCandidateType(candidate.type);
              } else {
                setEvents([]);
                setCandidateType(CandidateType.CANDIDATE_UNSPECIFIED);
              }
            })
            .catch((err) => logger.error("getFabricTransactions Failed", err));
        }
      }, WAIT),
    [fabricId, mode]
  );

  /**
   * This will subscribe to the FabricTransaction thread list.
   * It should trigger an updated when anything on the fabric
   * changes.
   */
  useEffect(() => {
    if (fabricId) {
      return Sextant.subscribeFabricTransactions(
        fabricId,
        (transactions: FabricTransaction[]) => {
          setFabricTransaction((prev) => {
            /**
             * To avoid over-fetching the same data, verify a new transaction
             * to spamming the API's, only refresh if there are new transactions.
             * then we refresh the fabricConfig but only if there was a change.
             */
            if (hasChanged(prev, transactions)) {
              refresh();
              return transactions;
            }
            return prev;
          });
        },
        (err) => logger.error(err)
      );
    }
    return MessageManager.unsubscribe;
  }, [fabricId, refresh]);

  useEffect(() => {
    refresh();
    window.cnc.setSync(refresh);
  }, [refresh]);

  useEffect(() => {
    // Connect the fabric specific websocket
    window.cnc.sextantPool.useStream(fabricId);
    return () => {
      /**
       * TORT-10920 UI: "No fabric data found" page is
       * shown up before loading the fabric page.
       *
       * Need to reset the loading flag for each
       * fabric or the "No fabric data found" error
       * us shown while loading.
       */
      setLoading(true);
      setFabricConfig(undefined);
      /**
       * websocket will disconnect itself once
       * all the subscribers are cleaned up.
       * should be no need for
       * window.cnc.sextantPool.close(fabricId);
       */
    };
  }, [fabricId]);

  const {
    getNode,
    getPortById,
    getPortByIndex,
    getPortByName,
    getPluggableByName
  } = useFabricHelpers(fabricConfig?.fabric);

  const { getDeviceByModelName, getDeviceModelNames, getDeviceModels } =
    useDeviceModels();

  const setMode = useCallback((m: CandidateMode) => {
    /* store the value in local storage so the user will have a consistent experience */
    localStorage.setItem(KEY_NAME, m);
    _setMode(m);
  }, []);

  // Determine the current context based on which ID is set
  const context: CandidateContext = useMemo(() => {
    if (fabricId) {
      return "fabric";
    } else if (multisiteId) {
      return "multisite";
    }
    return undefined;
  }, [fabricId, multisiteId]);

  // Array of VNI objects from the first tenant (excludes L3 VNIs)
  const vnis = useMemo(() => {
    return fabricConfig?.tenants?.[0]?.vnis.filter((vni) => !vni.isL3) ?? [];
  }, [fabricConfig?.tenants]);

  // Array of VRF objects from the first tenant
  const vrfs = useMemo(() => {
    return fabricConfig?.tenants?.[0]?.vrfs ?? [];
  }, [fabricConfig?.tenants]);

  const value = useMemo(
    () => ({
      loading,
      fabric: fabricConfig?.fabric,
      multiSites: fabricConfig?.multisites,
      softwareConfig: fabricConfig?.swConf,
      tenants: fabricConfig?.tenants,
      ipslaConfigs: fabricConfig?.tenants?.[0]?.ipSlaConfigs,
      perVlanStp: fabricConfig?.perVlanStp,
      events,
      candidateType,
      context,
      mode,
      vnis,
      vrfs,
      /**
       * This should not be needed but
       * the reality is the streaming api
       * doesn't stream everything we need.
       * this will let a component force a
       * reload of the fabric config.
       */
      refresh,
      setMode,
      setFabricId,
      setMultisiteId,
      configLimits,
      /* Node & Port Helper functions */
      getNode,
      getPortByName,
      getPortByIndex,
      getPortById,
      getPluggableByName,
      /* Device Model Helper functions */
      getDeviceByModelName,
      getDeviceModelNames,
      getDeviceModels
    }),
    [
      candidateType,
      configLimits,
      context,
      events,
      fabricConfig?.fabric,
      fabricConfig?.multisites,
      fabricConfig?.perVlanStp,
      fabricConfig?.swConf,
      fabricConfig?.tenants,
      getDeviceByModelName,
      getDeviceModelNames,
      getDeviceModels,
      getNode,
      getPluggableByName,
      getPortById,
      getPortByIndex,
      getPortByName,
      loading,
      mode,
      refresh,
      setMode,
      vnis,
      vrfs
    ]
  );

  return (
    <DataProviderContext.Provider value={value}>
      {children}
    </DataProviderContext.Provider>
  );
};
